import { useEffect, useRef } from "react";
import { useToast } from "@chakra-ui/react";
import { useQueryClient, type Query } from "@tanstack/react-query";

type TrackedQuery = Pick<Query, "queryHash" | "state">;

const SLOW_REQUEST_TIMEOUT_MS = 10000; // 10sec
const TOAST_DURATION_MS = 60000; // 60sec
const TOAST_MESSAGE = "Возможно, соединение с сетью нестабильно."
  + " Некоторые данные могут загружаться дольше";

/**
 * Считаем медленными только запросы первой загрузки.
 * Background refetch намеренно игнорируется.
 */
const isInitialLoading = (query: TrackedQuery) =>
  query.state.status === "pending"
  && query.state.fetchStatus === "fetching";

export function SlowLoadingProvider() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const toastShownRef = useRef(false);
  const timersRef = useRef(new Map<
      string,
      {
        timerId: ReturnType<typeof setTimeout>;
        failureCount: number;
      }
  >());

  useEffect(() => {
    const clearAllTimers = () => {
      for (const { timerId } of timersRef.current.values()) {
        clearTimeout(timerId);
      }

      timersRef.current.clear();
    };

    const startTracking = (query: TrackedQuery) => {
      if (toastShownRef.current) {
        return;
      }

      const { queryHash, state } = query;
      const { fetchFailureCount } = state;

      const existing = timersRef.current.get(queryHash);

      if (existing?.failureCount === fetchFailureCount) {
        return;
      }

      if (existing) {
        clearTimeout(existing.timerId);
      }

      const timerId = setTimeout(() => {
        timersRef.current.delete(queryHash);

        if (!isInitialLoading(query)) {
          return;
        }

        toastShownRef.current = true;

        toast({
          id: "slow-request",
          description: TOAST_MESSAGE,
          duration: TOAST_DURATION_MS,
        });

        clearAllTimers();
      }, SLOW_REQUEST_TIMEOUT_MS);

      timersRef.current.set(queryHash, {
        timerId,
        failureCount: fetchFailureCount,
      });
    };

    const stopTracking = (query: TrackedQuery) => {
      const { queryHash } = query;
      const existing = timersRef.current.get(queryHash);

      if (!existing) {
        return;
      }

      clearTimeout(existing.timerId);
      timersRef.current.delete(queryHash);
    };

    /**
     * Глобально отслеживаем изменения всех React Query запросов.
     * Это позволяет определить медленные initial-запросы
     * без необходимости изменять каждый useQuery.
     */
    const queryCache = queryClient.getQueryCache();
    const unsubscribe = queryCache.subscribe((event) => {
      if (event.type !== "updated") {
        return;
      }

      const query = event.query;

      if (isInitialLoading(query)) {
        startTracking(query);
      } else {
        stopTracking(query);
      }
    });

    return () => {
      unsubscribe();
      clearAllTimers();
    };
  }, [queryClient, toast]);

  return null;
}
